import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import AlertSettings from '../components/AlertSettings';
import { supabase } from '../config/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../contexts/ProfileContext';

interface Employee {
  id: string;
  name: string;
  email: string;
}

const AlertSettingsPage: React.FC = () => {
  const { user } = useAuth();
  const { profile } = useProfile();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (profile?.role === 'supervisor') {
      fetchEmployees();
    } else {
      setIsLoading(false);
    }
  }, [profile]);

  const fetchEmployees = async () => {
    try {
      console.log('Fetching employees...');
      const { data, error: fetchError } = await supabase
        .from('profiles')
        .select('id, name, email')
        .eq('role', 'employee');

      if (fetchError) {
        console.error('Error fetching employees:', fetchError);
        setError(fetchError.message);
        return;
      }

      if (data) {
        console.log('Found employees:', data);
        setEmployees(data);
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center py-8">Cargando...</div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-red-600 bg-red-50 p-4 rounded-md">
              Error: {error}
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!user || !profile) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center py-8 text-gray-500">
              Por favor, inicia sesión para acceder a esta página
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-2xl font-bold mb-6">Configuración de Alertas</h1>
          
          {/* Employee selector for supervisors */}
          {profile.role === 'supervisor' && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Seleccionar Empleado
              </label>
              <select
                value={selectedEmployee || ''}
                onChange={(e) => setSelectedEmployee(e.target.value)}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              >
                <option value="">Seleccionar un empleado...</option>
                {employees.map((employee) => (
                  <option key={employee.id} value={employee.id}>
                    {employee.name} ({employee.email})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Show settings for the selected employee or current user */}
          {((profile.role === 'supervisor' && selectedEmployee) || profile.role === 'employee') && (
            <AlertSettings employeeId={selectedEmployee || profile.id} />
          )}

          {profile.role === 'supervisor' && !selectedEmployee && (
            <div className="text-center py-8 text-gray-500">
              Por favor, selecciona un empleado para configurar sus alertas
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default AlertSettingsPage;
